export function normalizeCatalogUrl(url) {
  if (!url) return "";
  const raw = String(url).trim();
  if (!raw) return "";
  const noHash = raw.split("#")[0].split("?")[0];
  return noHash.replace(/^\/+/, "").replace(/\/+$/, "");
}

export function toInternalRoute(url) {
  if (!url) return "/";
  if (/^https?:\/\//i.test(url)) return url;
  const normalized = normalizeCatalogUrl(url);
  return normalized ? `/${normalized}/` : "/";
}

export function toSiteUrl(url, origin = "https://sharkfoto.com") {
  if (!url) return origin.replace(/\/+$/, "");
  if (/^https?:\/\//i.test(url)) return url;
  const normalized = normalizeCatalogUrl(url);
  const normalizedOrigin = origin.replace(/\/+$/, "");
  return normalized ? `${normalizedOrigin}/${normalized}/` : `${normalizedOrigin}/`;
}

export function isActiveIn(item, area) {
  return Array.isArray(item?.active) && item.active.includes(area);
}

export function menuGroupsForArea(menuData, area) {
  return (Array.isArray(menuData) ? menuData : [])
    .filter((group) => isActiveIn(group, area))
    .map((group) => ({
      ...group,
      children: (Array.isArray(group.children) ? group.children : [])
        .filter((item) => !item?.disabled && isActiveIn(item, area))
        .sort(
          (a, b) =>
            Number(a?.sortIndex ?? 9999) - Number(b?.sortIndex ?? 9999) ||
            String(a?.modelName || a?.title || "").localeCompare(String(b?.modelName || b?.title || ""))
        ),
    }))
    .filter((group) => group.children.length)
    .sort(
      (a, b) =>
        Number(a?.sortIndex ?? 9999) - Number(b?.sortIndex ?? 9999) ||
        String(a?.modelName || a?.title || "").localeCompare(String(b?.modelName || b?.title || ""))
    );
}

export function findMenuGroup(menuData, titleOrKey) {
  const target = String(titleOrKey || "").trim().toLowerCase();
  return (Array.isArray(menuData) ? menuData : []).find((group) => {
    return (
      String(group?.title || "").trim().toLowerCase() === target ||
      String(group?.key || "").trim().toLowerCase() === target
    );
  });
}

export function createProductIndexes(productList) {
  const byUrl = new Map();
  const byToolName = new Map();
  for (const product of Array.isArray(productList) ? productList : []) {
    if (!product?.enabled) continue;
    const url = normalizeCatalogUrl(product.url);
    if (url) byUrl.set(url, product);
    if (product.toolName) byToolName.set(String(product.toolName), product);
  }
  return { byUrl, byToolName };
}

export function productForMenuItem(item, productIndexes) {
  if (!item || !productIndexes) return null;
  return (
    productIndexes.byUrl.get(normalizeCatalogUrl(item.url)) ||
    productIndexes.byToolName.get(String(item.ref || "")) ||
    null
  );
}

function modelSort(a, b) {
  return (
    Number(a?.sortIndex ?? 9999) - Number(b?.sortIndex ?? 9999) ||
    String(a?.modelName || a?.title || "").localeCompare(
      String(b?.modelName || b?.title || "")
    )
  );
}

function groupSort(a, b) {
  return (
    Number(a?.sortIndex ?? a?.sortOrder ?? 9999) -
      Number(b?.sortIndex ?? b?.sortOrder ?? 9999) ||
    String(a?.title || "").localeCompare(String(b?.title || ""))
  );
}

export function modelGroups(modelList) {
  return (Array.isArray(modelList) ? modelList : [])
    .filter((group) => Array.isArray(group?.models))
    .map((group) => ({
      ...group,
      models: group.models
        .filter((model) => model?.enabled)
        .map((model) => ({
          ...model,
          groups: [group.title].filter(Boolean),
          groupTitle: group.title || "",
          groupId: group.id,
          groupSortIndex: Number(group.sortIndex ?? group.sortOrder ?? 9999),
        }))
        .sort(modelSort),
    }))
    .filter((group) => group.models.length)
    .sort(groupSort);
}

export function enabledModels(modelList) {
  const groups = modelGroups(modelList);
  if (groups.length) {
    return groups.flatMap((group) => group.models);
  }
  return (Array.isArray(modelList) ? modelList : [])
    .filter((model) => model?.enabled)
    .sort(modelSort);
}

export function modelGroupSortOrder(model, groupTitle) {
  if (Number.isFinite(Number(model?.groupSortIndex))) {
    return Number(model.groupSortIndex);
  }
  const orders = model?.groupSortOrders;
  if (orders && typeof orders === "object" && !Array.isArray(orders)) {
    const direct = Number(orders[groupTitle]);
    if (Number.isFinite(direct)) return direct;
    const normalizedTarget = String(groupTitle || "").trim().toLowerCase();
    for (const [key, value] of Object.entries(orders)) {
      if (String(key || "").trim().toLowerCase() === normalizedTarget) {
        const n = Number(value);
        if (Number.isFinite(n)) return n;
      }
    }
  }
  return 9999;
}
